import {
    Box, Button, Collapse, Flex, Input, InputGroup, InputLeftElement,
    List, ListItem, useColorModeValue, Text, useOutsideClick, IconButton
  } from '@chakra-ui/react';
  import { SearchIcon, ChevronRightIcon, ChevronDownIcon } from '@chakra-ui/icons';
  import { useRef, useState, useEffect } from 'react';
  
const CatalogueSelector = ({ dataCatalogues, onSelected = () => {}, prefilledId = '', context }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [manuallyExpanded, setManuallyExpanded] = useState({});
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const backgroundColor = useColorModeValue('white', 'gray.800');
    const inputBackground = useColorModeValue('brand.100', 'purple.500');
    const inputHover = useColorModeValue('#F5F3F9', 'purple.600');
    const searchHoverResultColor = useColorModeValue('gray.100', 'gray.600');
    const selectedBg = useColorModeValue('gray.200', 'purple.800');
  
    const hasAutoSelected = useRef(false);

    useEffect(() => {
      if (!hasAutoSelected.current && dataCatalogues?.data?.length > 0) {
        let foundCatalogue = null;
    
        for (const business of dataCatalogues.data) {
          const match = business?.owned_product_catalogs?.data?.find(cat => cat.id === prefilledId);
          if (match) {
            foundCatalogue = match;
            break;
          }
        }
    
        // Fallback to first available if prefilledId not matched
        if (!foundCatalogue) {
          for (const business of dataCatalogues.data) {
            const firstCatalogue = business?.owned_product_catalogs?.data?.[0];
            if (firstCatalogue) {
              foundCatalogue = firstCatalogue;
              break;
            }
          }
        }
    
        if (foundCatalogue) {
          setSelected(foundCatalogue);
          onSelected?.(foundCatalogue);
          hasAutoSelected.current = true;
        }
      }
    }, [dataCatalogues, onSelected, prefilledId]);
    
    
    const ref = useRef();
    useOutsideClick({
      ref,
      handler: () => {
        setIsOpen(false);
        setSearch('');
      }
    });
  
    const handleToggle = (id) => {
      setManuallyExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    };
  
    const handleSelect = (catalogue) => {
      setSelected(catalogue);
      onSelected?.(catalogue);
      setIsOpen(false);
      setSearch('');
    };
  
    // Filter businesses that have catalogues AND catalogues match the search term
    const filteredBusinesses = (dataCatalogues?.data || []).filter((item) => {
      const catalogues = item.owned_product_catalogs?.data || [];
      return catalogues.length > 0 &&
        catalogues.some(cat => cat.name?.toLowerCase().includes(search.toLowerCase()));
    });

    const expandSelectedCatalogueBusiness = () => {
        if (!selected || !dataCatalogues?.data?.length) return;
      
        for (const business of dataCatalogues.data) {
          const catalogueMatch = business.owned_product_catalogs?.data?.some(cat => cat.id === selected.id);
          if (catalogueMatch) {
            setManuallyExpanded(prev => ({
              ...prev,
              [business.id]: true,
            }));
            break;
          }
        }
      };
      
  
    return (
      <Box position="relative" ref={ref} width="100%">
        <Button
             onClick={() => {
                setIsOpen(!isOpen);
                if (!isOpen) expandSelectedCatalogueBusiness(); // expand on open
            }}
            w="100%"
            size="sm"
            justifyContent="space-between"
            variant="outline"
            isDisabled={context === 'update'}
            rightIcon={<ChevronDownIcon boxSize={5} />}
            bg={useColorModeValue('#f8f6fb', '#1e1530')} // inputBg light/dark
            color={useColorModeValue('#421e66', '#b5b5cd')} // text light/dark
            borderColor={useColorModeValue('#dcd3e6', '#524789')} // optional border color
            _hover={{
                bg: useColorModeValue('#f8f6fb', '#3c326f'), // light hover / tab background
            }}
            fontWeight="normal"
            >
            {selected ? selected.name : 'Select Catalogue'}
        </Button>
  
        <Collapse in={isOpen} animateOpacity>
          <Box
            mt={2}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={3}
            bg={backgroundColor}
            zIndex={9999}
            position="absolute"
            w="100%"
            maxH="300px"
            overflowY="auto"
            boxShadow="xl"
          >
           <InputGroup mb={2} alignItems="center">
            <InputLeftElement
                pointerEvents="none"
                height="100%"                     
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <SearchIcon color="gray.500" boxSize={4} />  
            </InputLeftElement>

            <Input
                placeholder="Search catalogues..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="sm"
                borderRadius="md"
                fontSize="sm"                     
            />
            </InputGroup>
  
            <List spacing={2}>
                {filteredBusinesses?.map((item) => {
                    const matchedCatalogues = item.owned_product_catalogs.data.filter(cat =>
                        cat.name?.toLowerCase().includes(search.toLowerCase())
                    );

                    // Auto-expand on search; otherwise use manual toggle
                    const isExpanded = search.length > 0 || manuallyExpanded[item.id];

                    return (
                    <Box key={item.id}>
                        <Flex
                            align="center"
                            justify="space-between"
                            bg={inputBackground}
                            px={2}
                            borderRadius="md"
                            _hover={{ bg: inputHover }}
                            cursor="pointer"
                            onClick={() => handleToggle(item.id)}
                        >
                        <Text fontWeight="medium" fontSize={'13px'}>{item.name}</Text>
                        <IconButton
                            size="sm"
                            variant="ghost"
                            icon={isExpanded ? <ChevronDownIcon  boxSize={4}/> : <ChevronRightIcon boxSize={4}/>}
                            aria-label="Toggle"
                            onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(item.id);
                            }}
                        />
                        </Flex>

                        <Collapse in={isExpanded} animateOpacity>
                        <List spacing={1} mt={1} pl={2}>
                            {matchedCatalogues.map((cat) => (
                            <ListItem
                                    key={cat.id}
                                    px={2}
                                    py={1}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    borderRadius="md"
                                    bg={selected?.id === cat.id ? selectedBg : 'transparent'}
                                    cursor="pointer"
                                    _hover={{ bg: selected?.id === cat.id ? selectedBg : searchHoverResultColor }}
                                    onClick={() => handleSelect(cat)}
                            >
                            {/* LEFT: Radio + Dot + Name/ID */}
                            <Flex align="center" gap={3}>
                                {/* Radio */}
                                <Box
                                    boxSize="14px"             // outer circle size
                                    minW="14px"
                                    borderRadius="full"
                                    bg={selected?.id === cat.id ? '#8671cc' : 'white'} // outer fill
                                    border={'2px solid'}
                                    borderColor={selected?.id === cat.id ? '#8671cc' : 'gray.200'}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    >
                                    {selected?.id === cat.id && (
                                        <Box
                                            boxSize="4px"           // inner white dot
                                            borderRadius="full"
                                            bg="white"
                                        />
                                    )}
                                    </Box>
                                {/* Dot */}
                                <Box
                                    boxSize="10px"
                                    borderRadius="full"
                                    bg={cat.external_event_sources?.data?.length > 0 ? 'green.500' : 'red.500'}
                                />
                            
                                {/* Text */}
                                <Box>
                                    <Text fontWeight="medium" noOfLines={1} fontSize={'12px'}>{cat.name}</Text>
                                    <Text noOfLines={1} fontSize={'10px'}>ID: {cat.id}</Text>
                                </Box>
                            </Flex>
                            
                            {/* RIGHT: Action required (only if red) */}
                            {cat.external_event_sources?.data?.length > 0 ? null : (
                                <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    bg="red.500"
                                    color={'white'}
                                    px={1}
                                    py={0.2}
                                    borderRadius="xl"
                                >
                                    Action required
                                </Text>
                                )}

                                </ListItem>
                        
                            ))}
                        </List>
                        </Collapse>
                    </Box>
                    );
                })}

                {filteredBusinesses.length === 0 && (
                    <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
                    No catalogues found.
                    </Text>
                )}
            </List>

          </Box>
        </Collapse>
      </Box>
    );
  };
  
  export default CatalogueSelector;
  